import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Filter, RefreshCw, Plus, X, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOR = {
  gold: '#d4a94a',
  black: '#c9c9c9',
  diamond: '#7ad8e3',
  vip: '#c471ed',
  daypass: '#94a3b8',
};

const SOURCE_LABEL = {
  wia_plan: 'PLANO',
  member: 'SÓCIO',
  cortesia: 'CORTESIA',
};

const SOURCE_STYLE = {
  wia_plan: 'text-cyan-300 bg-cyan-400/10 border-cyan-400/30',
  member:   'text-emerald-300 bg-emerald-400/10 border-emerald-400/30',
  cortesia: 'text-fuchsia-300 bg-fuchsia-400/10 border-fuchsia-400/30',
};

export default function WIAAdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [error, setError] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [showCortesia, setShowCortesia] = useState(false);
  const [revoking, setRevoking] = useState(null); // phone_e164 currently revoking

  const revokeCortesia = async (phone) => {
    if (!window.confirm(`Revogar acesso W IA de ${phone}?`)) return;
    setRevoking(phone);
    try {
      const token = localStorage.getItem('wclub_admin_token');
      await axios.delete(`${API}/wia/admin/cortesia/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao revogar cortesia');
    } finally {
      setRevoking(null);
    }
  };

  // Cortesia form state
  const [cName, setCName] = useState('');
  const [cPhone, setCPhone] = useState('+55');
  const [cTier, setCTier] = useState('gold');
  const [cDays, setCDays] = useState(30);
  const [cSubmitting, setCSubmitting] = useState(false);
  const [cSuccess, setCSuccess] = useState(null);
  const [cError, setCError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('wclub_admin_token');
      if (!token) { setError('Login como admin necessário.'); setLoading(false); return; }
      const params = new URLSearchParams();
      if (tierFilter) params.set('tier', tierFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      if (expiringOnly) params.set('expiring_in_days', '7');
      const res = await axios.get(`${API}/wia/admin/subscriptions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tierFilter, sourceFilter, expiringOnly]);

  const triggerReminders = async () => {
    setSendingReminders(true);
    try {
      const token = localStorage.getItem('wclub_admin_token');
      await axios.post(`${API}/wia/admin/send-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao disparar lembretes.');
    } finally {
      setSendingReminders(false);
    }
  };

  const submitCortesia = async () => {
    setCError(null); setCSuccess(null);
    setCSubmitting(true);
    try {
      const token = localStorage.getItem('wclub_admin_token');
      const res = await axios.post(`${API}/wia/admin/cortesia`, {
        phone: cPhone, tier: cTier, days: cDays, name: cName || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCSuccess(`✓ ${res.data.phone_e164} · ${res.data.tier.toUpperCase()} · válido até ${new Date(res.data.valid_until).toLocaleDateString('pt-BR')}`);
      setCName(''); setCPhone('+55'); setCDays(30);
      await load();
    } catch (e) {
      setCError(e?.response?.data?.detail || 'Erro ao criar cortesia');
    } finally {
      setCSubmitting(false);
    }
  };

  const formatBRL = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="wia-admin-back"
            className="flex items-center gap-2 text-white/60 hover:text-white transition text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>WClub</span>
          </button>
          <div className="text-xs tracking-widest font-['Jost'] text-cyan-400/80">W IA · ADMIN</div>
          <button
            onClick={load}
            data-testid="wia-admin-refresh"
            className="p-2 rounded-full hover:bg-white/5 transition"
            title="Atualizar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-cyan-400' : 'text-white/60'}`} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-8" data-testid="wia-admin-page">
        {error && (
          <div data-testid="wia-admin-error"
            className="mb-6 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm"
          >{error}</div>
        )}

        {/* KPIs — 3 cols on desktop, 3 tighter cols on mobile */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 mb-6 md:mb-8">
          <Kpi label="COM ACESSO" value={data?.active_count ?? '—'} testId="kpi-active" />
          <Kpi label="MRR (PAGO)" value={formatBRL(data?.mrr_brl)} testId="kpi-mrr" />
          <Kpi label="ARR (PAGO)" value={formatBRL(data?.arr_brl)} testId="kpi-arr" />
        </div>

        {/* Filters row 1 — Tier */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Filter className="w-4 h-4 text-white/40" />
          <TierChip label="Todos"   value=""        current={tierFilter} onClick={setTierFilter} />
          <TierChip label="Gold"    value="gold"    current={tierFilter} onClick={setTierFilter} />
          <TierChip label="Black"   value="black"   current={tierFilter} onClick={setTierFilter} />
          <TierChip label="Diamond" value="diamond" current={tierFilter} onClick={setTierFilter} />
          <TierChip label="VIP"     value="vip"     current={tierFilter} onClick={setTierFilter} />
        </div>

        {/* Filters row 2 — Source + actions */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <SourceChip label="Origem: Todos"  value=""          current={sourceFilter} onClick={setSourceFilter} />
          <SourceChip label="Sócios"         value="member"    current={sourceFilter} onClick={setSourceFilter} />
          <SourceChip label="Planos pagos"   value="wia_plan"  current={sourceFilter} onClick={setSourceFilter} />
          <SourceChip label="Cortesias"      value="cortesia"  current={sourceFilter} onClick={setSourceFilter} />

          <button
            data-testid="wia-admin-expiring-toggle"
            onClick={() => setExpiringOnly(v => !v)}
            className={`px-3 py-1 rounded-full text-xs font-['Jost'] tracking-wide transition ${
              expiringOnly
                ? 'bg-amber-400/20 text-amber-300 border border-amber-400/40'
                : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'
            }`}
          >
            {expiringOnly ? '⏱ Vencendo em 7d' : 'Ver todos'}
          </button>

          <div className="flex-1" />

          <button
            data-testid="wia-admin-add-cortesia"
            onClick={() => setShowCortesia(true)}
            className="px-4 py-1.5 rounded-full text-xs font-['Jost'] tracking-wide bg-fuchsia-400/10 text-fuchsia-300 border border-fuchsia-400/30 hover:bg-fuchsia-400/20 transition inline-flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" /> Adicionar cortesia
          </button>

          <button
            data-testid="wia-admin-send-reminders"
            onClick={triggerReminders}
            disabled={sendingReminders}
            className="px-4 py-1.5 rounded-full text-xs font-['Jost'] tracking-wide bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/20 transition disabled:opacity-50"
          >
            {sendingReminders ? 'Enviando…' : '🔔 Lembretes'}
          </button>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-white/5 overflow-hidden">
          {/* Desktop header — hidden on mobile */}
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-[10px] tracking-widest font-['Jost'] text-white/40 bg-white/[0.02]">
            <div className="col-span-3">NOME · TELEFONE</div>
            <div className="col-span-2">TIER</div>
            <div className="col-span-2">ORIGEM</div>
            <div className="col-span-2">VALOR/EXPIRA</div>
            <div className="col-span-1 text-right">DIAS</div>
            <div className="col-span-1 text-right">STATUS</div>
            <div className="col-span-1 text-right"></div>
          </div>
          {loading && !data && (
            <div className="p-8 text-center text-white/40 text-sm">Carregando…</div>
          )}
          {data && data.subscriptions.length === 0 && (
            <div data-testid="wia-admin-empty" className="p-8 text-center text-white/40 text-sm">
              Nenhuma linha para esse filtro.
            </div>
          )}
          {data?.subscriptions.map((s, i) => {
            const tooltip = s.source === 'cortesia' && s.granted_by_admin
              ? `Concedido por ${s.granted_by_admin}${s.created_at ? ' em ' + new Date(s.created_at).toLocaleDateString('pt-BR') : ''}`
              : undefined;
            const daysLabel = !s.valid_until ? '∞'
              : s.days_left < 0 ? `${Math.abs(s.days_left)}d atrás`
              : `${s.days_left}d`;
            const daysColor = !s.valid_until ? 'text-white/30'
              : s.days_left < 0 ? 'text-white/30'
              : s.days_left <= 7 ? 'text-amber-400'
              : 'text-white/70';
            const valueLabel = s.source === 'wia_plan'
              ? formatBRL((s.amount_centavos || 0) / 100)
              : (s.valid_until ? new Date(s.valid_until).toLocaleDateString('pt-BR') : 'permanente');

            return (
              <div
                key={`${s.phone_e164}-${s.source}-${i}`}
                data-testid={`wia-admin-row-${s.phone_e164}`}
                className="border-t border-white/5 hover:bg-white/[0.02] transition"
              >
                {/* Mobile card */}
                <div className="md:hidden p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-white/90 text-sm font-['Jost'] truncate">
                        {s.member_name || '—'}
                      </div>
                      <div className="font-['Space_Mono'] text-white/50 text-[11px] tabular-nums truncate">
                        {s.phone_e164 || '—'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`inline-block w-2 h-2 rounded-full ${s.is_active ? 'bg-emerald-400' : 'bg-white/20'}`} />
                      {s.source === 'cortesia' && (
                        <button
                          onClick={() => revokeCortesia(s.phone_e164)}
                          disabled={revoking === s.phone_e164}
                          data-testid={`revoke-${s.phone_e164}`}
                          title="Revogar cortesia"
                          className="p-1.5 rounded-full text-rose-400/70 hover:text-rose-300 hover:bg-rose-500/10 transition disabled:opacity-30"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    <span
                      className="font-semibold tracking-widest"
                      style={{ color: TIER_COLOR[s.tier] || '#fff' }}
                    >
                      {(s.tier || '—').toUpperCase()}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[9px] tracking-widest border ${SOURCE_STYLE[s.source] || ''}`}
                      title={tooltip}
                    >
                      {SOURCE_LABEL[s.source] || s.source?.toUpperCase()}
                    </span>
                    <span className="text-white/50 tabular-nums ml-auto">{valueLabel}</span>
                    <span className={`${daysColor} tabular-nums`}>· {daysLabel}</span>
                  </div>
                </div>

                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-sm items-center">
                  <div className="col-span-3 min-w-0">
                    <div className="text-white/90 text-xs truncate">{s.member_name || '—'}</div>
                    <div className="font-['Space_Mono'] text-white/50 text-[11px] tabular-nums truncate">{s.phone_e164 || '—'}</div>
                  </div>
                  <div className="col-span-2" style={{ color: TIER_COLOR[s.tier] || '#fff' }}>
                    {(s.tier || '—').toUpperCase()}
                  </div>
                  <div className="col-span-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-[9px] tracking-widest border ${SOURCE_STYLE[s.source] || ''}`}
                      title={tooltip}
                    >
                      {SOURCE_LABEL[s.source] || s.source?.toUpperCase()}
                    </span>
                  </div>
                  <div className="col-span-2 text-white/60 text-xs">{valueLabel}</div>
                  <div className={`col-span-1 text-right tabular-nums text-xs ${daysColor}`}>{daysLabel}</div>
                  <div className="col-span-1 text-right">
                    <span className={`inline-block w-2 h-2 rounded-full ${s.is_active ? 'bg-emerald-400' : 'bg-white/20'}`} />
                  </div>
                  <div className="col-span-1 text-right">
                    {s.source === 'cortesia' && (
                      <button
                        onClick={() => revokeCortesia(s.phone_e164)}
                        disabled={revoking === s.phone_e164}
                        data-testid={`revoke-desktop-${s.phone_e164}`}
                        title="Revogar cortesia"
                        className="p-1.5 rounded-full text-rose-400/70 hover:text-rose-300 hover:bg-rose-500/10 transition disabled:opacity-30"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cortesia modal */}
      {showCortesia && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowCortesia(false)}
          data-testid="wia-cortesia-modal"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl border border-white/10 p-6"
            style={{
              background: 'linear-gradient(180deg,rgba(30,20,45,.95) 0%,rgba(10,5,20,.98) 100%)',
              backdropFilter: 'blur(24px)',
            }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-['Jost'] text-fuchsia-300 tracking-wide">Adicionar cortesia</h3>
              <button onClick={() => setShowCortesia(false)} className="p-1 rounded-full hover:bg-white/5">
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>

            <label className="block text-white/40 text-[10px] tracking-widest font-['Jost'] mb-1">NOME (OPCIONAL)</label>
            <input
              type="text" value={cName} onChange={e => setCName(e.target.value)}
              data-testid="cortesia-name"
              placeholder="Ex: Bruno Severino"
              className="w-full px-4 py-3 mb-4 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-fuchsia-400/40 transition"
            />

            <label className="block text-white/40 text-[10px] tracking-widest font-['Jost'] mb-1">TELEFONE (E.164)</label>
            <input
              type="tel" value={cPhone} onChange={e => setCPhone(e.target.value)}
              data-testid="cortesia-phone"
              placeholder="+55 47 99953-7338"
              className="w-full px-4 py-3 mb-4 rounded-xl bg-white/[0.04] border border-white/10 text-white font-['Space_Mono'] text-sm tabular-nums placeholder-white/20 focus:outline-none focus:border-fuchsia-400/40 transition"
            />

            <label className="block text-white/40 text-[10px] tracking-widest font-['Jost'] mb-1">TIER</label>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {['gold','black','diamond','vip'].map(t => (
                <button
                  key={t} onClick={() => setCTier(t)}
                  data-testid={`cortesia-tier-${t}`}
                  className={`py-2 rounded-xl text-xs tracking-wider font-['Jost'] transition ${
                    cTier === t
                      ? 'border-2'
                      : 'border border-white/10 text-white/50 hover:border-white/25'
                  }`}
                  style={{
                    borderColor: cTier === t ? TIER_COLOR[t] : undefined,
                    color: cTier === t ? TIER_COLOR[t] : undefined,
                    background: cTier === t ? `${TIER_COLOR[t]}12` : undefined,
                  }}
                >
                  {t.toUpperCase()}
                </button>
              ))}
            </div>

            <label className="block text-white/40 text-[10px] tracking-widest font-['Jost'] mb-1">DURAÇÃO (DIAS)</label>
            <div className="flex items-center gap-2 mb-5">
              {[30, 90, 180, 365].map(d => (
                <button
                  key={d} onClick={() => setCDays(d)}
                  data-testid={`cortesia-days-${d}`}
                  className={`flex-1 py-2 rounded-xl text-xs font-['Jost'] transition ${
                    cDays === d
                      ? 'bg-fuchsia-400/20 text-fuchsia-300 border border-fuchsia-400/40'
                      : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/25'
                  }`}
                >{d}d</button>
              ))}
              <input
                type="number" value={cDays} onChange={e => setCDays(Number(e.target.value)||0)}
                data-testid="cortesia-days-input"
                className="w-20 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs text-center font-['Space_Mono'] focus:outline-none focus:border-fuchsia-400/40"
              />
            </div>

            {cError && <div className="text-rose-400 text-xs mb-3" data-testid="cortesia-error">{cError}</div>}
            {cSuccess && <div className="text-emerald-400 text-xs mb-3" data-testid="cortesia-success">{cSuccess}</div>}

            <button
              onClick={submitCortesia}
              disabled={cSubmitting}
              data-testid="cortesia-submit"
              className="w-full py-3 rounded-xl text-sm tracking-wide font-medium bg-fuchsia-500 text-white hover:bg-fuchsia-400 transition disabled:opacity-50"
            >
              {cSubmitting ? 'Salvando…' : 'Conceder acesso'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Kpi = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl p-3 md:p-5 border border-white/5"
    style={{ background: 'linear-gradient(180deg,rgba(255,255,255,.02),rgba(255,255,255,0))' }}
  >
    <div className="text-[9px] md:text-[10px] tracking-widest font-['Jost'] text-white/40 mb-1 md:mb-2 leading-tight">{label}</div>
    <div className="text-lg md:text-2xl font-light text-white tabular-nums truncate">{value}</div>
  </div>
);

const TierChip = ({ label, value, current, onClick }) => (
  <button
    data-testid={`tier-chip-${value || 'all'}`}
    onClick={() => onClick(value)}
    className={`px-3 py-1 rounded-full text-xs font-['Jost'] transition ${
      current === value
        ? 'bg-white/15 text-white border border-white/20'
        : 'bg-white/[0.03] text-white/50 border border-white/10 hover:border-white/20'
    }`}
  >
    {label}
  </button>
);

const SourceChip = ({ label, value, current, onClick }) => (
  <button
    data-testid={`source-chip-${value || 'all'}`}
    onClick={() => onClick(value)}
    className={`px-3 py-1 rounded-full text-[10px] tracking-wider font-['Jost'] transition ${
      current === value
        ? 'bg-cyan-400/20 text-cyan-200 border border-cyan-400/40'
        : 'bg-white/[0.03] text-white/40 border border-white/10 hover:border-white/20'
    }`}
  >
    {label}
  </button>
);
